// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
define(['uikit', 'lodash4', 'react', '/www/lum/pub/styled.js', 'prop-types',
    '/www/lum/pub/uikit.util.js', '/www/lum/pub/util/zmixpanel.js',
    '/www/lum/pub/comp/ga.js', '/util/dialing_codes.js',
    '/svc/compliance/pub/ext_util.js', '/util/date.js'],
    (UIKit, _, React, styled, PT, {getCommonProps}, zmixpanel, ga,
    dialingCodes, complUtil, zdate)=>
{
const {useContext, useEffect, useMemo, useState, useCallback,
    forwardRef} = React;
const {allowed_countries: countryList} = complUtil;
const {Layout, Typography, Tooltip, Input, IconButton, Icon} = UIKit;
const {Flex} = Layout;
const {Label} = Typography;
const {Textbox, RadioGroup: UIKitRadioGroup, Checkbox: UIKitCheckbox,
    DatePicker: UIKitDatePicker, Dropdown: UIKitDropdown, Toggle: UIKitToggle,
    RadioTab: UIKitRadioTab, Number: UIKitNumber, Phone: UIKitPhone} = Input;
const {dialing_code_list, dialing_code_rlist} = dialingCodes;
const {useTheme} = styled;

const convertValue = (dir, val)=>{
    const convert = v=>{
        if (!v)
            return;
        let d = zdate(v);
        if (dir=='out')
        {
            d = zdate.align(new Date(d.getFullYear(), d.getMonth(),
                d.getDate(), 12, 0, 0, 0), 'DAY');
        }
        else
        {
            d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(),
                d.getUTCDate(), 12, 0, 0, 0));
        }
        return d;
    };
    if (_.isObject(val) && !(val instanceof Date))
    {
        if (!val.from&&!val.to)
            return;
        return {from: convert(val.from), to: convert(val.to)};
    }
    return convert(val);
};

const withTracking = WrappedComp=>{
    const WithTracking = forwardRef((props, ref)=>{
        const {onChange, track, ...rest} = props;
        const handleChange = useCallback(value=>{
            if (_.isFunction(onChange))
            {
                onChange(value);
                if (track?.mixpanelId)
                    zmixpanel.track(`${track?.mixpanelId} Changed`, {value});
            }
        }, [onChange, track?.mixpanelId]);
        return <WrappedComp ref={ref} {...rest} onChange={handleChange}/>;
    });
    WithTracking.displayName = `WithTracking(${WrappedComp.displayName
        ||WrappedComp.name||'Component'})`;
    return WithTracking;
};

const DatePicker = forwardRef((props, ref)=>{
    const {
        value: _value,
        onChange: _onChange,
        canClear, onClear,
        rightInnerContent: _rightInnerContent,
    } = props;
    const value = convertValue('in', _value);
    const onChange = val=>_onChange?.(convertValue('out', val));
    const rightInnerContent = useMemo(()=>{
        if (!canClear)
            return _rightInnerContent;
        return <IconButton icon="Close" size="xs" onClick={onClear}/>;
    }, [canClear, onClear, _rightInnerContent]);
    return <UIKitDatePicker
      ref={ref}
      {...props}
      value={value}
      onChange={onChange}
      rightInnerContent={rightInnerContent}
    />;
});
DatePicker.displayName = 'CpDatePicker';

const Password = props=>{
    const {disabled, rightInnerContent} = props;
    const [hide, setHide] = useState(true);
    useEffect(()=>{
        if (disabled)
            setHide(true);
    }, [disabled]);
    const _rightInnerContent = <Flex gap="02" align_items="center">
        {!disabled && <IconButton size="xs" icon={hide ? 'EyeOn' : 'EyeOff'}
          onClick={()=>setHide(h=>!h)} type="button"/>}
        {rightInnerContent}
    </Flex>;
    return <StyledTextbox
      {...props}
      rightInnerContent={_rightInnerContent}
      inputProps={{type: hide?'password':'text'}}
    />;

};
Password.displayName = 'Password';
Password.propTypes = {
    value: PT.string,
    onChange: PT.func,
    onFocus: PT.func,
    onBlur: PT.func,
    placeholder: PT.string,
    disabled: PT.bool,
    invalid: PT.bool,
    label: PT.node,
    helperText: PT.node,
    size: PT.oneOf(['sm', 'md', 'lg']),
};

const Search = forwardRef((props, ref)=>{
    const {value, onChange, shrink: shrink_mode, size} = props;
    const [shrink, setShrink] = useState(!value&&shrink_mode);
    // make sure external value changes unshrink search
    useEffect(()=>{
        if (shrink_mode && !!value && shrink)
            setShrink(false);
    }, [value, shrink, shrink_mode]);
    const onClearClick = ()=>onChange?.('');
    if (shrink)
        return <IconButton icon="Search" onClick={()=>setShrink(false)}/>;
    return <StyledTextbox
      ref={ref}
      onBlur={(...args)=>{
          if (!value&&shrink_mode)
              setShrink(true);
          props.onBlur?.(...args);
      }}
      autoFocus={shrink_mode}
      size={size}
      {...props}
      leftInnerContent={<Icon
        name="Search"
        color="icon"
        size={size=='lg' ? 'sm' : 'xs'}
      />}
      rightInnerContent={<IconButton
        size="xs"
        icon="Close"
        onClick={onClearClick}
        style={{visibility: value?'visible':'hidden'}}
      />}
    />;
});
Search.displayName = 'Search';
Search.propTypes = {
    value: PT.string,
    onChange: PT.func,
    onFocus: PT.func,
    onBlur: PT.func,
    placeholder: PT.string,
    disabled: PT.bool,
    invalid: PT.bool,
    label: PT.node,
    helperText: PT.node,
    size: PT.oneOf(['sm', 'md', 'lg']),
    shrink: PT.bool,
};

const StyledTextbox = styled(Textbox)`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
      background-color: ${({theme: {color}})=>color.bg} !important;
      color: ${({theme: {color}})=>color.text} !important;
      transition: all 5000s ease-in-out 0s;
      transition-property: background-color, color;
  }
`;

const Flag = props=><div className="selected_flag f19"
  title={`${props.label}: ${props.callingCode}`}>
  <span className={`flag ${props.value.toLowerCase()}`}/>
</div>;

const phoneCountries = _.map(countryList, (label, value)=>({label, value,
    callingCode: dialing_code_list[value]}))
    .filter(c=>!!c.callingCode);

const getCountryFromNumber = number=>{
    if (!number.startsWith('+'))
        return;
    let country, tryCode = number.slice(1);
    while (tryCode && !country)
    {
        country = dialing_code_rlist[tryCode];
        tryCode = tryCode.slice(0, -1);
    }
    return country;
};

const Phone = forwardRef((props, ref)=>{
    let {label, labelIcon, required, helperText, invalid,
        onChange, onBlur, disabled, placeholder = '+1 123-123-12345',
        menuPortalTarget, necessityIndicator, ...rest} = props;
    const commonProps = getCommonProps(rest);
    const value = props.value || '';
    const phoneProps = {label, labelIcon, required, helperText, invalid, value,
        onChange, onBlur, disabled, placeholder, menuPortalTarget,
        necessityIndicator};
    return <UIKitPhone {...commonProps} {...phoneProps} ref={ref}
      countries={phoneCountries}
      parsePhoneNumber={number=>{
          const country = getCountryFromNumber(number);
          const callingCode = country&&dialing_code_list[country];
          if (country && callingCode)
          {
              const prefix = `+${callingCode}`;
              let formattedOutput = number.replace(prefix, '').trim();
              formattedOutput = !formattedOutput ? number :
                  `${prefix} ${formattedOutput}`;
              return {country, callingCode, formattedOutput};
          }
      }}
      getCountryFlag={country=>country?<Flag {...country}/>:null}
    />;
});
Phone.displayName = 'CpPhone';

const CountryLabel = props=>{
    const {value, name, text} = props;
    const fl = value.toLowerCase();
    return <Flex className={`f19 ${fl}`} data-testid={name}
      align_items="center" gap="8px" name={name}>
      <span className={`flag ${fl}`}/>
      <span>{text}</span>
    </Flex>;
};

const countryOptions = _.map(countryList, (label, value)=>({
    label:
        <CountryLabel value={value} name={label} text={`${value}: ${label}`}/>,
    value,
    label_orig: label,
}));

const getStyleProps = (props, name, classNamesState)=>{
    const {cx, getStyles, getClassNames, className} = props;
    return {
        $css: _.omit(getStyles(name, props), 'label'),
        className: cx(classNamesState??{}, getClassNames(name, props),
            className),
    };
};

const CountryOption = props=>{
    const {
        children,
        isDisabled,
        isFocused,
        isSelected,
        innerRef,
        innerProps,
    } = props;
    return <OptionItem
      {...getStyleProps(props, 'option', {
        option: true,
        'option--is-disabled': isDisabled,
        'option--is-focused': isFocused,
        'option--is-selected': isSelected,
      })}
      ref={innerRef}
      aria-disabled={isDisabled}
      {..._.omit(innerProps, 'onMouseMove', 'onMouseOver')}
      role="menuitem"
    >
      <Checkbox
        checked={isSelected}
        disabled={isDisabled}
        onChange={()=>null}
      />
      <TextOverflow>
        {children}
      </TextOverflow>
    </OptionItem>;
};

const OptionItem = styled.div`${prop=>prop.$css}`;

const TextOverflow = styled.div`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const countryOptionStyles = ({font_family, font_weight, font_size,
  line_height, color,
})=>
  (baseStyles, {isFocused, isDisabled})=>({
    ...baseStyles,
    fontFamily: font_family.sans,
    fontStyle: 'normal',
    fontWeight: font_weight.regular,
    fontSize: font_size.sm,
    lineHeight: line_height.none,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '40px',
    minHeight: '40px',
    padding: '0px 12px',
    boxSizing: 'border-box',
    backgroundColor: isFocused ? color.bg_fill_primary_secondary : undefined,
    color: isDisabled ? color.text_disabled : color.text_tertiary,
    '&:hover': {
        backgroundColor: color.bg_fill_secondary,
    }
  });

const CountrySelector = forwardRef(
    ({countries, shortValue, multiselect, clearable, emptyOptionText,
        ...rest}, ref)=>
{
    const _theme = useTheme();
    const options = useMemo(()=>{
        const list = countries
            ? countryOptions.filter(o=>countries.includes(o.value))
            : countryOptions;
        if (!clearable)
            return list;
        return [
            {
                value: '',
                label: <CountryLabel value="" name="random"
                    text={emptyOptionText || 'Random country'}/>,
                label_orig: emptyOptionText || 'Random country',
            },
            ...list,
        ];
    }, [countries, clearable]);
    const filterOption = (option, inputValue)=>{
        const {label, value} = option;
        let name = label.props.name;
        return value.toLowerCase().includes(inputValue.toLowerCase())||
            name.toLowerCase().includes(inputValue.toLowerCase());
    };
    const formatOptionLabel = (option, {context})=>{
        const val = context=='value' && shortValue ? 'value' : 'label_orig';
        return <CountryLabel value={option.value} name={option.label_orig}
          text={option[val]}/>;
    };
    const [components, styles] = useMemo(()=>multiselect ? [
        {Option: CountryOption},
        {option: countryOptionStyles(_theme)}
    ] : [], [multiselect, _theme]);
    return <Dropdown
      {...rest}
      ref={ref}
      options={options}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      multiselect={multiselect}
      components={components}
      clearable={clearable}
      styles={styles}
    />;
});
CountrySelector.displayName = 'CountrySelector';
CountrySelector.propTypes = {
    value: PT.any,
    onChange: PT.func,
    countries: PT.arrayOf(PT.string),
    label: PT.node,
    labelIcon: PT.string,
    required: PT.bool,
    helperText: PT.node,
};

const Checkbox = forwardRef((props, ref)=>{
    const {onClick, onChange, disabled, track, checked, ...rest} = props;
    const context = useContext(ga.Ga_context);
    const handleClick = e=>{
        if (disabled)
            return;
        const newValue = !checked;
        if (track?.mixpanelId)
        {
            const eventName = newValue ? 'checked' : 'unchecked';
            zmixpanel.track(`${track.mixpanelId} ${eventName}`);
        }
        if (track?.gaId)
        {
            const eventName = newValue ? 'checkbox_check' : 'checkbox_uncheck';
            ga.trigger(eventName, context, props);
        }
        onChange(newValue, props.id);
        onClick?.(e);
    };
    return <UIKitCheckbox
      ref={ref}
      onClick={handleClick}
      disabled={disabled}
      checked={checked}
      {...rest}
    />;
});
Checkbox.displayName = 'CpCheckbox';
Checkbox.propTypes = {
    track: PT.shape({
        gaId: PT.string,
        mixpanelId: PT.string,
    }),
};

const CheckboxGroup = props=>{
    const {
        options,
        value,
        onChange,
        disabled,
        track,
        label,
        tooltip,
        variant = 'row',
    } = props;
    const context = useContext(ga.Ga_context);
    const commonProps = getCommonProps(props);
    const onCheckBoxChange = (v, id)=>{
        onChange({...value, [id]: v}, id);
        if (track?.gaId)
            ga.trigger('checkbox_grp_change', context, props);
    };
    return <Flex flex_direction="column" gap="8px" {...commonProps}>
      {label &&<Tooltip tooltip={tooltip}>
        <Label variant="lg" color="text_secondary">{label}</Label>
      </Tooltip>}
      <Flex flex_direction={variant} gap="8px" flex_wrap="wrap">
        {options.map(opt=>{
            const oTrack = {
                mixpanelId: track?.mixpanelId?
                    `${track.mixpanelId} ${opt.track?.mixpanelId||opt.label}`:
                    undefined,
                gaId: opt.track?.gaId,
            };
            return <Checkbox
              key={opt.value}
              {...opt}
              checked={value[opt.value]}
              onChange={v=>onCheckBoxChange(v, opt.value)}
              disabled={disabled||opt.disabled}
              track={oTrack}
            />;
        })}
      </Flex>
    </Flex>;
};
CheckboxGroup.displayName = 'CheckboxGroup';
CheckboxGroup.propTypes = {
    value: PT.object,
    label: PT.node,
    onChange: PT.func,
    disabled: PT.bool,
    options: PT.arrayOf(PT.shape({
        value: PT.string,
        label: PT.node,
        disabled: PT.bool,
    })),
    variant: PT.oneOf(['row', 'column']),
};

const RadioGroup = forwardRef((props, ref)=>{
    const {onChange, disabled} = props;
    const handleChange = (val, opt, evt)=>{
        if (disabled)
            return;
        if (opt.track?.mixpanelId)
            zmixpanel.track(opt.track.mixpanelId);
        onChange?.(val, opt, evt);
    };
    return <UIKitRadioGroup ref={ref} {...props} onChange={handleChange} />;
});
RadioGroup.displayName = 'CpRadioGroup';

const RadioTab = forwardRef((props, ref)=>{
    const {onChange, disabled, track} = props;
    const context = useContext(ga.Ga_context);
    const handleChange = (val, opt, evt)=>{
        if (disabled)
            return;
        if (track?.mixpanelId)
            zmixpanel.track(track.mixpanelId);
        if (track?.gaId)
            ga.trigger(track.gaId, context, props);
        onChange?.(val, opt, evt);
    };
    return <UIKitRadioTab ref={ref} {...props} onChange={handleChange} />;
});
RadioTab.displayName = 'CpRadioTab';

const Dropdown = forwardRef((props, ref)=>{
    const {multiselect, options, value, getOptionValue} = props;
    const val = useMemo(()=>{
        if (!_.isArray(options) || _.isNil(value) || _.isPlainObject(value) ||
            _.isArray(value) && value.every(_.isPlainObject))
        {
            return value;
        }
        const arr = _.isArray(value) ? value : [value];
        // handle groups
        const flat_options = options
            .map(o=>_.isArray(o.options) ? o.options : o).flat();
        const cb = opt=>arr.includes(getOptionValue ?
            getOptionValue(opt) : opt.value);
        if (_.isArray(value) || multiselect)
            return flat_options.filter(cb);
        return flat_options.find(cb);
    }, [options, value]);
    return <UIKitDropdown
      ref={ref}
      classNamePrefix="uikit-dropdown"
      {...props}
      value={val}
    />;
});
Dropdown.displayName = 'CpDropdown';
Dropdown.Embedded = UIKitDropdown.Embedded;

const ToggleComp = forwardRef((props, ref)=>{
    let {ga_id, value, onClick, onMouseEnter, ...rest} = props;
    ga_id = `toggle_${ga_id}_${value}`;
    const handleClick = ga.hook_fn(onClick, 'click', {ga_id});
    const handleMouseEnter = ga.hook_fn(onMouseEnter, 'hover', {ga_id});
    return <UIKitToggle
      ref={ref}
      {...rest}
      value={value}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    />;
});
ToggleComp.displayName = 'CpToggle';
const Toggle = withTracking(ToggleComp);

const EyeToggle = forwardRef((props, ref)=>{
    let {ga_id, value, onClick: _onClick, onMouseEnter, ...rest} = props;
    ga_id = `eyetoggle_${ga_id}_${value}`;
    const onClick = useCallback(()=>_onClick(!value), [value, _onClick]);
    const handleClick = ga.hook_fn(onClick, 'click', {ga_id});
    const handleMouseEnter = ga.hook_fn(onMouseEnter, 'hover', {ga_id});
    const icon = value ? 'EyeOn' : 'EyeOff';
    const tooltip = value ? 'Hide' : 'Show';
    return <IconButton ref={ref} {...rest} value={value} icon={icon}
      onClick={handleClick} onMouseEnter={handleMouseEnter}
      tooltip={tooltip} size="xs" active={value}/>;
});
EyeToggle.displayName = 'CpEyeToggle';

const Number = withTracking(UIKitNumber);

return {
    ...Input,
    Number,
    Password,
    Search,
    Phone,
    CountrySelector,
    Checkbox,
    CheckboxGroup,
    RadioGroup,
    RadioTab,
    DatePicker,
    Dropdown,
    Toggle,
    EyeToggle,
};
});
